import { useEffect, useState } from "react";
import { FileText, Download, Maximize2, AlertTriangle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const isImage = (rc) => (rc.file.kind === "heic" ? true : (rc.file.content_type || "").startsWith("image/"));

const useBlobUrl = (url) => {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let live = true;
    let obj;
    setState({ loading: true });
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); })
      .then((b) => { if (live) { obj = URL.createObjectURL(b); setState({ url: obj }); } })
      .catch(() => live && setState({ error: true }));
    return () => { live = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  return [state, () => setState({ error: true })];
};

const DownloadButton = ({ rc, testid }) => (
  <Button variant="outline" asChild className="gap-1.5">
    <a href={api.receiptFileUrl(rc.id, true)} data-testid={testid}><Download className="h-4 w-4" /> Original herunterladen</a>
  </Button>
);

const PreviewError = ({ rc, h }) => (
  <div data-testid={`receipt-viewer-error-${rc.id}`} className={`flex w-full flex-col items-center justify-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-6 text-center ${h}`}>
    <AlertTriangle className="h-8 w-8 text-amber-500" />
    <p className="text-sm font-medium text-slate-800">Vorschau konnte nicht geladen werden.</p>
    <p className="text-sm text-slate-600">Die Originaldatei lässt sich trotzdem herunterladen.</p>
    <DownloadButton rc={rc} testid={`receipt-viewer-error-download-${rc.id}`} />
  </div>
);

export const ReceiptViewer = ({ rc, tall }) => {
  const [s, fail] = useBlobUrl(api.receiptPreviewUrl(rc.id));
  const h = tall ? "h-[75vh]" : "h-96";
  if (s.error) return <PreviewError rc={rc} h={h} />;
  if (s.loading) return <div data-testid={`receipt-viewer-loading-${rc.id}`} className={`flex w-full items-center justify-center rounded-lg border border-slate-200 bg-white ${h}`}><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return isImage(rc) ? (
    <img src={s.url} alt={rc.file.original_filename} onError={fail} data-testid={`receipt-viewer-${rc.id}`}
      className={`w-full rounded-lg border border-slate-200 bg-white object-contain ${tall ? "max-h-[75vh]" : "max-h-96"}`} />
  ) : (
    <iframe src={s.url} title={rc.file.original_filename} data-testid={`receipt-viewer-${rc.id}`}
      className={`w-full rounded-lg border border-slate-200 bg-white ${h}`} />
  );
};

export const ReceiptThumb = ({ rc, onClick }) => {
  const [broken, setBroken] = useState(false);
  return (
    <button onClick={onClick} data-testid={`receipt-thumb-${rc.id}`}
      className="flex h-20 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50 hover:border-slate-400">
      {isImage(rc) && !broken
        ? <img src={api.receiptPreviewUrl(rc.id)} alt="" onError={() => setBroken(true)} className="h-full w-full object-cover" />
        : <FileText className="h-7 w-7 text-slate-400" />}
    </button>
  );
};

export const ReceiptPreviewDialog = ({ rc, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl" data-testid="receipt-preview-dialog">
      <DialogHeader><DialogTitle className="truncate pr-6">{rc.file.original_filename}</DialogTitle></DialogHeader>
      {open && <ReceiptViewer rc={rc} tall />}
      <div className="flex justify-end"><DownloadButton rc={rc} testid="receipt-download-link" /></div>
    </DialogContent>
  </Dialog>
);

export const PreviewButtons = ({ rc, onPreview }) => (
  <div className="flex gap-1">
    <Button variant="ghost" size="sm" onClick={onPreview} data-testid={`receipt-preview-btn-${rc.id}`} className="h-8 gap-1 px-2 text-slate-600"><Maximize2 className="h-3.5 w-3.5" />Ansehen</Button>
    <Button variant="ghost" size="sm" asChild className="h-8 gap-1 px-2 text-slate-600">
      <a href={api.receiptFileUrl(rc.id, true)} data-testid={`receipt-download-btn-${rc.id}`}><Download className="h-3.5 w-3.5" />Download</a>
    </Button>
  </div>
);
